'use client';

import { datadogRum } from '@datadog/browser-rum';
import { createContext, useEffect, useMemo } from 'react';
import { v4 } from 'uuid';

import type { Environment } from '@/types';
import type { ReactNode } from 'react';

const logsEnabled =
  process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID &&
  process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;

export type Logger = {
  log: (message: string) => void;
  error: (error: Error) => void;
};

const LoggerContext = createContext<Logger | null>(null);

export const useLogger = (): Logger => {
  // NB: no actual state is currently stored in context
  // this may change once we add correlation ID mapping
  return {
    log: (message) => {
      console.info(message);

      if (logsEnabled) {
        datadogRum.addAction(message);
      }
    },
    error: (error) => {
      console.error(error.message);

      if (logsEnabled) {
        datadogRum.addError(error);
      }
    },
  };
};

export const LoggerProvider = ({
  env,
  children,
}: {
  children?: ReactNode;
  env: Environment;
}) => {
  useMemo(() => {
    if (logsEnabled) {
      datadogRum.init({
        applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID!,
        clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN!,
        site: 'datadoghq.com',
        service: 'b2c-public-website',
        env: env as string,
        version: process.env.NEXT_PUBLIC_BUILD_VERSION,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        allowedTracingUrls: [
          {
            match: 'https://www.b2c-public-website-dev.powershop.com.au/api',
            propagatorTypes: ['datadog'],
          },
          {
            match:
              'https://www.draft.b2c-public-website-dev.powershop.com.au/api',
            propagatorTypes: ['datadog'],
          },
          {
            match:
              'https://www.singlegas.b2c-public-website-dev.powershop.com.au/api',
            propagatorTypes: ['datadog'],
          },
          {
            match:
              'https://www.b2c-public-website-nonprod.powershop.com.au/api',
            propagatorTypes: ['datadog'],
          },
          {
            match: 'https://www.powershop.com.au/api',
            propagatorTypes: ['datadog'],
          },
        ],
      });
    }
  }, [env]);

  useEffect(() => {
    // https://stackoverflow.com/questions/56028913/usememo-vs-useeffect-usestate
    // I had this in the memo but under next it does not work
    window.sessionStorage.setItem(
      'sessionId',
      datadogRum.getInternalContext()?.session_id ?? v4()
    );
  }, []);

  return (
    <LoggerContext.Provider value={null}>{children}</LoggerContext.Provider>
  );
};
